// Franja de avisos de pedidos que se quedan cortos (o sobran) en Compras y
// Almacén > Compra de la semana (usuario, 05-oct-2026).
//
// Una vez pedido, si entra un evento nuevo o cambia uno en los días ya
// pedidos, cada proveedor con algo que avisar sale con sus cortos (lo que
// falta sobre lo ya pedido), sus sobrantes (solo informa) y los eventos
// nuevos. Botones: "Ver la diferencia" (filtra la tabla por el proveedor con
// "Solo lo que falta"), "PDF" y "WhatsApp" del pedido complementario (solo lo
// que falta) y "Marcar revisado". Tras el PDF o el WhatsApp ofrece marcar el
// pedido complementario como pedido (se suma a lo ya pedido).
//
// Props:
// - avisos: respuesta de GET /lista-compra-v2/avisos-pedidos.
// - onVerDiferencia(proveedor), onPdf(proveedor), onWhatsapp(proveedor) (promesa
//   con true si se generó), onMarcarComplementario(proveedor), onRevisado(proveedor).
import React, { useState } from 'react';
import { ddmm, formatCantidad, proveedoresConCortos, textoResumenAvisos } from './listaCompraUtils';

const claseBoton = 'h-[28px] px-2.5 rounded-lg border text-[12px] font-bold whitespace-nowrap bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed';

const textoEventos = (numeros) => ((numeros || []).length > 0 ? ` · ${numeros.length === 1 ? 'evento' : 'eventos'} ${numeros.join(', ')}` : '');

const AvisosPedidosFranja = ({
  avisos = null, onVerDiferencia = null, onPdf = null, onWhatsapp = null, onMarcarComplementario = null,
  onRevisado = null,
}) => {
  const [ocupados, setOcupados] = useState({}); // {proveedor_id: true}
  const [ofertas, setOfertas] = useState({}); // {proveedor_id: true} "¿Marcar como pedido?"

  const proveedores = avisos?.proveedores || [];
  if (proveedores.length === 0) return null;
  const conCortos = proveedoresConCortos(avisos);
  const sobrantes = proveedores.filter(p => (p.sobrantes || []).length > 0);

  const ejecutar = async (p, accion, ofrecer = false) => {
    if (!accion) return;
    setOcupados(prev => ({ ...prev, [p.proveedor_id]: true }));
    try {
      const ok = await accion(p);
      if (ok && ofrecer && onMarcarComplementario) setOfertas(prev => ({ ...prev, [p.proveedor_id]: true }));
    } finally {
      setOcupados(prev => ({ ...prev, [p.proveedor_id]: false }));
    }
  };

  const cerrarOferta = (id) => setOfertas((prev) => {
    const r = { ...prev };
    delete r[id];
    return r;
  });

  return (
    <div className="rounded-[12px] border border-[#F4D9AE] bg-[#FFF8EC] px-3 py-2.5 flex flex-col gap-2" data-testid="avisos-pedidos-franja">
      <div className="text-sm font-extrabold text-[#7A3A06]" data-testid="avisos-pedidos-resumen">
        {conCortos.length > 0 ? textoResumenAvisos(conCortos) : 'Compras: pedidos ya hechos revisados'}
        {sobrantes.length > 0 && (
          <span className="font-semibold text-[#5B6B64]">
            {' '}· sobra en {sobrantes.length} {sobrantes.length === 1 ? 'proveedor' : 'proveedores'}
          </span>
        )}
      </div>
      {proveedores.map((p) => {
        const id = p.proveedor_id;
        const hayCortos = (p.cortos || []).length > 0;
        const ocupado = !!ocupados[id];
        return (
          <div key={id} className="rounded-lg bg-white border border-[#F1E3C8] px-2.5 py-2 flex flex-col gap-1" data-testid={`avisos-pedidos-${id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-extrabold text-[#16221D]">{p.proveedor_nombre}</span>
                {p.dias_pedidos && (
                  <span className="text-[11px] text-[#5B6B64]">pedido para {ddmm(p.dias_pedidos.desde)}{p.dias_pedidos.hasta !== p.dias_pedidos.desde ? ` - ${ddmm(p.dias_pedidos.hasta)}` : ''}</span>
                )}
                {p.revisado && <span className="px-1.5 rounded text-[11px] font-bold bg-[#E3F3EC] text-[#0F5E43]">Revisado</span>}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {hayCortos && onVerDiferencia && (
                  <button type="button" onClick={() => onVerDiferencia(p)} className={claseBoton} data-testid={`avisos-pedidos-ver-${id}`}>Ver la diferencia</button>
                )}
                {hayCortos && onPdf && (
                  <button type="button" onClick={() => ejecutar(p, onPdf, true)} disabled={ocupado} className={claseBoton} title={`Pedido complementario a ${p.proveedor_nombre}: solo lo que falta`} data-testid={`avisos-pedidos-pdf-${id}`}>PDF</button>
                )}
                {hayCortos && onWhatsapp && (
                  <button type="button" onClick={() => ejecutar(p, onWhatsapp, true)} disabled={ocupado} className={claseBoton} title={`Copiar el pedido complementario a ${p.proveedor_nombre}`} data-testid={`avisos-pedidos-whatsapp-${id}`}>WhatsApp</button>
                )}
                {!p.revisado && onRevisado && (
                  <button type="button" onClick={() => ejecutar(p, onRevisado)} disabled={ocupado} className={claseBoton} data-testid={`avisos-pedidos-revisado-${id}`}>Marcar revisado</button>
                )}
              </div>
            </div>
            {ofertas[id] && (
              <div className="flex flex-wrap items-center justify-end gap-2 rounded-lg bg-[#E7EEFB] px-2.5 py-1.5 text-[12px] text-[#1E3A8A]" data-testid={`avisos-pedidos-marcar-${id}`}>
                <span>¿Marcar el pedido complementario como pedido? Se suma a lo ya pedido.</span>
                <button
                  type="button"
                  onClick={async () => { await ejecutar(p, onMarcarComplementario); cerrarOferta(id); }}
                  disabled={ocupado}
                  className={claseBoton}
                  data-testid={`avisos-pedidos-marcar-si-${id}`}
                >
                  Marcar como pedido
                </button>
                <button type="button" onClick={() => cerrarOferta(id)} className="font-semibold hover:underline">No</button>
              </div>
            )}
            {hayCortos && (
              <ul className="text-[12px] text-[#4A5A53] flex flex-col gap-0.5">
                {p.cortos.map(c => (
                  <li key={c.ingrediente_id} data-testid={`avisos-pedidos-corto-${id}-${c.ingrediente_id}`}>
                    <span className="font-semibold text-[#8A1C12]">Falta</span> {c.texto_falta || `${formatCantidad(c.falta)} ${c.unidad || ''}`} de {c.nombre}
                    {' '}<span className="text-[#5B6B64]">(pedido {formatCantidad(c.pedido)}, necesario {formatCantidad(c.necesario)} {c.unidad || ''}{textoEventos(c.eventos)})</span>
                  </li>
                ))}
              </ul>
            )}
            {(p.sobrantes || []).length > 0 && (
              <ul className="text-[12px] text-[#4A5A53] flex flex-col gap-0.5">
                {p.sobrantes.map(s => (
                  <li key={s.ingrediente_id} data-testid={`avisos-pedidos-sobrante-${id}-${s.ingrediente_id}`}>
                    <span className="font-semibold text-[#1E3A8A]">Sobra</span> {s.texto_sobra || `${formatCantidad(s.sobra)} ${s.unidad || ''}`} de {s.nombre}
                    {' '}<span className="text-[#5B6B64]">(pedido {formatCantidad(s.pedido)}, necesario {formatCantidad(s.necesario)} {s.unidad || ''}{textoEventos(s.eventos)})</span>
                  </li>
                ))}
              </ul>
            )}
            {(p.eventos_nuevos || []).length > 0 && (
              <div className="text-[12px] text-[#4A5A53]">
                Eventos nuevos en estos días: {p.eventos_nuevos.map(e => `${e.numero}${e.nombre_evento ? ` ${e.nombre_evento}` : ''} (${ddmm(e.fecha)})`).join(', ')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AvisosPedidosFranja;
